import {Component, Host, Prop, h, forceUpdate} from "@stencil/core";

import {i18nProvider} from "@trumpf/ux.i18n";
import {translations} from "./i18n";

import {Topic, EditFooter} from "./notification-settings.sub";
import {CategoriesFC} from "./Categories.sub";
import {AssetsFC} from "./Assets.sub";

import {userSettingsStore} from "../stores/userSettingsStore";
import type {Stage} from "../stores/apiService";

const {i18n} = i18nProvider;

/**
 * Notification settings component to manage user notification settings.
 * @slot greeter_headline - default greeter headline
 * @slot greeter_text - default greeter text
 *
 * @slot categories_headline - categories specific headline
 * @slot categories_text - categories specific text
 *
 * @slot categories_table_heading - category column header
 * @slot categories_table_notification_channel_heading - channel column header
 * @slot categories_table_email_channel_heading - channel column header
 *
 * @slot categories_save_button_label - save-button label
 * @slot categories_cancel_button_label - cancel-button label
 *
 * @slot assets_headline - assets specific headline
 * @slot assets_text - assets specific text
 *
 * @slot assets_table_heading - assets column header
 *
 * @slot assets_save_button_label - save-button label
 * @slot assets_cancel_button_label - cancel-button label
 */
@Component({
	tag: "uxn-notification-settings",
	styleUrl: "notification-settings.scss",
	shadow: false,
})
export class NotificationSettings {
	/** content of notifications tooltip */
	@Prop() notificationsTooltip: string = "";
	/** content of email tooltip */
	@Prop() emailTooltip: string = "";
	/** locale */
	@Prop() locale: string = "de_DE";
	/** stage */
	@Prop() stage: Stage = "PROD";
	/** toggle debugging */
	@Prop() debug: boolean = false;

	#i18nSubscriberId: string;

	async componentWillLoad() {
		this.#customTranslation();
		await i18nProvider.changeLanguage(this.locale);
		this.#i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
	}

	async componentDidLoad() {
		await userSettingsStore.init({
			locale: this.locale,
			stage: this.stage.toUpperCase() as Stage,
			debug: this.debug,
		});
	}

	disconnectedCallback() {
		i18nProvider.unsubscribe(this.#i18nSubscriberId);
		userSettingsStore.reset();
	}

	#customTranslation() {
		Object.entries(translations).forEach(([language, bundle]) => i18n.addResourceBundle(language, "user-settings", bundle));

		i18n.on("languageChanged", (lng) => {
			if (Object.keys(translations).includes(lng)) return;

			const lngCode = lng.split("_")[0];
			const fallbackLng = Object.keys(translations).find((key) => key.startsWith(lngCode));
			if (fallbackLng) i18n.changeLanguage(fallbackLng);
		});
	}

	render() {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return (
			<Host class="uxn-notification-settings gr-8 push-2 no-gutter gr-10@t push-1@t gr-10@m push-1@m no-gutter@m">
				<Topic headlineSlot="greeter_headline" textSlot="greeter_text" />
				<CategoriesFC
					topic={{headlineSlot: "categories_headline", textSlot: "categories_text"}}
					table={{
						categoryHeaderSlot: "categories_table_heading",
						notificationChannel: {headerSlot: "categories_table_notification_channel_heading", tooltip: this.notificationsTooltip},
						eMailChannel: {headerSlot: "categories_table_email_channel_heading", tooltip: this.emailTooltip},
					}}
					categories={userSettingsStore.categories}
				>
					<EditFooter
						saveButtonSlot="categories_save_button_label"
						cancelButtonSlot="categories_cancel_button_label"
						hasChanges={userSettingsStore.categories.hasChanges}
						hasFailedLoading={userSettingsStore.categories.hasFailedGet || userSettingsStore.categories.hasFailedPost}
						updateCallback={userSettingsStore.updateCategories}
						resetCallback={userSettingsStore.resetCategories}
					/>
				</CategoriesFC>
				<AssetsFC
					topic={{headlineSlot: "assets_headline", textSlot: "assets_text"}}
					table={{headerSlot: "assets_table_heading"}}
					assets={userSettingsStore.assets}
				>
					<EditFooter
						saveButtonSlot="assets_save_button_label"
						cancelButtonSlot="assets_cancel_button_label"
						hasChanges={userSettingsStore.assets.hasChanges}
						hasFailedLoading={userSettingsStore.assets.hasFailedGet || userSettingsStore.assets.hasFailedPost}
						updateCallback={userSettingsStore.updateAssets}
						resetCallback={userSettingsStore.resetAssets}
					/>
				</AssetsFC>
			</Host>
		);
	}
}
